/**
 * Interceptador Global de Rede Nativo (XMLHttpRequest e Fetch).
 *
 * Garante que TODAS as requisições HTTP da aplicação — inclusive as feitas por:
 * 1. Bibliotecas instaladas (como asap-upload, asap-crud, plugins legados ou uploads diretos)
 * 2. Microserviços carregados via iframe (rotas com prefixo '-/...')
 * herdem automaticamente o cabeçalho 'X-Device-Context: asap_authorized_access'
 * ao se comunicarem com a API da ASAP, evitando o erro 403 Forbidden.
 */

export function isAsapApiUrl(url: string): boolean {
  if (!url) {
    return false;
  }
  return (
    url.indexOf('tecnologiaasap.com.br') !== -1 ||
    url.indexOf('plataformaasap.com.br') !== -1 ||
    url.indexOf('hmgasap.com.br') !== -1 ||
    url.indexOf('admin-microservice-') !== -1 ||
    url.indexOf('/api/') !== -1
  );
}

/**
 * Aplica a interceptação nativa de rede (XMLHttpRequest e fetch) em qualquer objeto window (janela principal ou iframe).
 */
export function patchWindowNetwork(win: any) {
  if (!win || win._asapNetworkSecurityPatched) {
    return;
  }
  win._asapNetworkSecurityPatched = true;

  // 1. Interceptação de XMLHttpRequest
  if (win.XMLHttpRequest) {
    try {
      const xhrProto = win.XMLHttpRequest.prototype;
      const originalOpen = xhrProto.open;
      const originalSend = xhrProto.send;

      xhrProto.open = function(method: string, url: string) {
        this._asapRequestUrl = url;
        return originalOpen.apply(this, arguments);
      };

      xhrProto.send = function() {
        try {
          const url = this._asapRequestUrl || '';
          if (isAsapApiUrl(url)) {
            this.setRequestHeader('X-Device-Context', 'asap_authorized_access');
          }
        } catch (e) {
          // Ignora caso o estado do XHR já tenha sido fechado ou header já configurado
        }
        return originalSend.apply(this, arguments);
      };
    } catch (e) {
      console.warn('[Security] Não foi possível aplicar patch no XMLHttpRequest da janela:', e);
    }
  }

  // 2. Interceptação de window.fetch
  if (win.fetch) {
    try {
      const originalFetch = win.fetch;
      win.fetch = function(input: any, init: any) {
        init = init || {};
        const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');

        if (isAsapApiUrl(url)) {
          if (!init.headers) {
            init.headers = {};
          }

          if (typeof Headers !== 'undefined' && init.headers instanceof Headers) {
            init.headers.set('X-Device-Context', 'asap_authorized_access');
          } else if (Array.isArray(init.headers)) {
            init.headers.push(['X-Device-Context', 'asap_authorized_access']);
          } else {
            init.headers['X-Device-Context'] = 'asap_authorized_access';
          }
        }

        return originalFetch.call(this, input, init);
      };
    } catch (e) {
      console.warn('[Security] Não foi possível aplicar patch no fetch da janela:', e);
    }
  }
}

/**
 * Sincroniza dados essenciais de autenticação (tokens, client) com a janela de um microservice/iframe
 */
export function syncIframeContext(win: any) {
  if (!win) return;
  try {
    // Sincroniza sessionStorage se disponível
    if (typeof sessionStorage !== 'undefined' && win.sessionStorage) {
      const keys = ['token', 'client', 'loggedUser', 'plainToken'];
      keys.forEach(k => {
        const val = sessionStorage.getItem(k);
        if (val && !win.sessionStorage.getItem(k)) {
          win.sessionStorage.setItem(k, val);
        }
      });
    }

    // Envia mensagem via postMessage caso o microserviço escute eventos
    if (win.postMessage) {
      win.postMessage({
        type: 'ASAP_DEVICE_CONTEXT',
        header: 'X-Device-Context',
        value: 'asap_authorized_access',
        token: typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null,
        client: typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('client') : null
      }, '*');
    }
  } catch (e) {
    // Ignora restrições entre origens distintas
  }
}

// Aplica na janela principal
if (typeof window !== 'undefined') {
  patchWindowNetwork(window);

  // Monitora dinamicamente a inserção de iframes (como microserviços) no DOM
  try {
    const patchIframeElement = (iframe: any) => {
      try {
        const setup = () => {
          try {
            if (iframe && iframe.contentWindow) {
              patchWindowNetwork(iframe.contentWindow);
              syncIframeContext(iframe.contentWindow);
            }
          } catch (e) {}
        };

        setup();
        iframe.addEventListener('load', setup);

        // Polling rápido para pegar chamadas disparadas no carregamento inicial do microservice
        const timers = [50, 150, 300, 600, 1200, 2500];
        timers.forEach(delay => setTimeout(setup, delay));
      } catch (e) {}
    };

    // Monitora novos iframes via MutationObserver
    const MutationObserverConstructor = (window as any).MutationObserver || (window as any).WebKitMutationObserver;
    if (MutationObserverConstructor) {
      const observer = new MutationObserverConstructor((mutations: any[]) => {
        if (mutations && mutations.forEach) {
          mutations.forEach((mutation: any) => {
            if (mutation && mutation.addedNodes) {
              for (let i = 0; i < mutation.addedNodes.length; i++) {
                const node: any = mutation.addedNodes[i];
                if (node && node.tagName === 'IFRAME') {
                  patchIframeElement(node);
                } else if (node && node.querySelectorAll) {
                  const iframes = node.querySelectorAll('iframe');
                  for (let j = 0; j < iframes.length; j++) {
                    patchIframeElement(iframes[j]);
                  }
                }
              }
            }
          });
        }
      });

      observer.observe(document.documentElement || document.body, {
        childList: true,
        subtree: true
      });
    }
  } catch (e) {
    console.warn('[Security] Não foi possível ativar observer de iframes:', e);
  }
}

console.log('[Security] Interceptador global nativo de rede ativado para aplicação e microserviços.');



// WEBPACK FOOTER //
// ./src/app/@core/interceptors/global-network-security.ts